import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  runTransaction as runTx,
  getDocs,
  getCountFromServer,
  type Firestore,
  addDoc,
  collection,
} from 'firebase/firestore'
import { FirestoreServiceBase, type WithId } from './FirestoreServiceBase'
import { type QuerySpec } from '@/types/FirestoreQuery.ts'
import { buildClientQuery } from '@/utils/queryBuilder.ts'
import { globalConverter, convertToFirestore } from './converter'

export class FirestoreServiceClient extends FirestoreServiceBase {
  constructor(private readonly db: Firestore) {
    super()
  }

  async get<T>(path: string): Promise<WithId<T> | null> {
    const ref = doc(this.db, path).withConverter(globalConverter<T>())
    const snap = await getDoc(ref)
    return snap.exists() ? (snap.data() as WithId<T>) : null
  }

  async add<T>(collectionPath: string, data: T) {
    const col = collection(this.db, collectionPath).withConverter(globalConverter<T>())
    const ref = await addDoc(col, data as T)
    return ref.id
  }

  async set<T>(path: string, data: T, merge = true) {
    const ref = doc(this.db, path).withConverter(globalConverter<T>())
    await setDoc(ref, data as T, { merge })
  }

  async update<T>(path: string, data: Partial<T>) {
    const ref = doc(this.db, path).withConverter(globalConverter<T>())
    // Firestore's updateDoc doesn't use the converter; convert manually
    const convertedData = convertToFirestore(data) as { [x: string]: any }
    await updateDoc(ref, convertedData)
  }

  async remove(path: string) {
    const ref = doc(this.db, path).withConverter(globalConverter<unknown>())
    await deleteDoc(ref)
  }

  async runQuery<T>(spec: QuerySpec<T>): Promise<Array<WithId<T>>> {
    const q = buildClientQuery<T>(this.db, spec).withConverter(globalConverter<T>())
    const snap = await getDocs(q)
    return snap.docs.map((d) => d.data() as WithId<T>)
  }

  async runTransaction<T>(fn: (tx: unknown) => Promise<T>): Promise<T> {
    return runTx(this.db, async (tx) => fn(tx))
  }

  // Collection-based convenience methods
  async getDoc<T>(collectionPath: string, id: string): Promise<WithId<T> | null> {
    return this.get<T>(`${collectionPath}/${id}`)
  }

  async updateDoc<T>(collectionPath: string, id: string, data: Partial<T>): Promise<void> {
    return this.update<T>(`${collectionPath}/${id}`, data)
  }

  async deleteDoc(collectionPath: string, id: string): Promise<void> {
    return this.remove(`${collectionPath}/${id}`)
  }

  async count<T>(spec: QuerySpec<T>): Promise<number> {
    const q = buildClientQuery<T>(this.db, spec).withConverter(globalConverter<T>())
    const snap = await getCountFromServer(q)
    return snap.data().count
  }
}
